import { X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { FixtureResult, ReportBundleDiagnostic } from '@/reports/bundles/types';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { BundleFixtures } from '../BundleFixtures';
import { BundleProblems } from '../BundleProblems';
import { IconButton } from '../studioChrome';

export type CheckTab = 'problems' | 'tests';

/** The panel under the editor: the problems the last save found, and how each fixture came out. */
export function ChecksPanel({ tab, onTab, diagnostics, results, onSelect, onHide }: {
  tab: CheckTab;
  onTab: (tab: CheckTab) => void;
  diagnostics: ReportBundleDiagnostic[];
  results: Array<FixtureResult & { pending?: boolean }>;
  onSelect: (path: string) => void;
  onHide: () => void;
}) {
  const failing = results.filter((r) => !r.ok).length;
  // held: made and held; one whose printed checks are still being made is not counted yet
  const held = results.filter((r) => r.ok && !r.pending).length;
  return (
    <section aria-label={I18n.get('designerChecks')} className="flex h-[12rem] shrink-0 flex-col border-t bg-background">
      <Tabs value={tab} onValueChange={(v) => onTab(v as CheckTab)} className="h-full min-h-0 gap-0">
        <div className="flex h-8 shrink-0 items-center justify-between border-b pr-1 pl-2">
          <TabsList variant="line" className="h-full">
            <TabsTrigger value="problems" className="px-2 text-xs">
              {I18n.get('reportBundleProblems')}
              {diagnostics.length > 0 && <Badge variant="destructive" className="h-4 px-1.5 text-[0.65rem]">{diagnostics.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="tests" className="px-2 text-xs">
              {I18n.get('designerTests')}
              {results.length > 0 && <Badge variant={failing ? 'destructive' : 'outline'} className="h-4 px-1.5 text-[0.65rem]">{held}/{results.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <IconButton label={I18n.get('designerHideChecks')} size="icon-xs" onClick={onHide}><X /></IconButton>
        </div>
        <TabsContent value="problems" className="min-h-0 overflow-auto"><BundleProblems diagnostics={diagnostics} onSelect={onSelect} /></TabsContent>
        <TabsContent value="tests" className="min-h-0 overflow-auto"><BundleFixtures results={results} onSelect={onSelect} /></TabsContent>
      </Tabs>
    </section>
  );
}
